import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    listassetcategories,
    createassetcategory,
    updateassetcategory,
    deleteassetcategory
} from "../../../../services/admin";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

export default function AssetCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        category_name: "",
        category_code: "",
        depreciation_method: "Straight Line",
        depreciation_rate: "",
        useful_life_years: "",
        description: "",
        is_active: true
    });

    const columnDefs = [
        { headerName: "Category Code", field: "category_code", width: 150, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "left" }, cellRenderer: (params) => <strong>{params.value}</strong> },
        { headerName: "Category Name", field: "category_name", width: 200, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "left" } },
        { headerName: "Depreciation Method", field: "depreciation_method", width: 180, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "left" }, cellRenderer: (params) => <span className="badge bg-info">{params.value}</span> },
        { headerName: "Rate (%)", field: "depreciation_rate", width: 120, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "center" }, cellRenderer: (params) => `${params.value}%` },
        { headerName: "Useful Life", field: "useful_life_years", width: 130, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "center" }, cellRenderer: (params) => params.value ? `${params.value} years` : '-' },
        {
            headerName: "Assets", field: "asset_count", width: 120, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "center" }, cellRenderer: (params) => {
                const data = params.data;

                return (
                    <div>
                        <span className="badge bg-primary">
                            {params.value || 0}
                        </span>

                        {data?.active_asset_count > 0 && (
                            <div>
                                <small className="text-success">
                                    {data.active_asset_count} active
                                </small>
                            </div>
                        )}
                    </div>
                );
            }
        },
        { headerName: "Status", field: "is_active", width: 120, sortable: true, resizable: true, filter: true, floatingFilter: true, cellStyle: { textAlign: "center" }, cellRenderer: (params) => params.value ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span> },
        {
            headerName: "Actions", field: "id", width: 150, sortable: false, resizable: true, filter: false, floatingFilter: false, cellStyle: { textAlign: "center" }, cellRenderer: (params) => {
                const data = params.data;

                return (
                    <div>
                        <button
                            className="btn btn-sm btn-primary me-1"
                            onClick={() => window.handleEditAssetCategory(params.value)}
                            title="Edit Category"
                        >
                            <i className="fa fa-edit"></i>
                        </button>

                        {data?.asset_count === 0 && (
                            <button
                                className="btn btn-sm btn-danger"
                                onClick={() => window.handleDeleteAssetCategory(params.value)}
                                title="Delete Category"
                            >
                                <i className="fa fa-trash"></i>
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    useEffect(() => {
        window.handleEditAssetCategory = (id) => { const category = categories.find(c => c.id === id); if (category) handleEdit(category); };
        window.handleDeleteAssetCategory = (id) => { const category = categories.find(c => c.id === id); if (category) handleDelete(category); };
        fetchCategories();
        return () => { delete window.handleEditAssetCategory; delete window.handleDeleteAssetCategory; };
    }, [categories]);

    const fetchCategories = () => {
        setLoading(true);
        listassetcategories().then((res) => {
            if (res.status === true) {
                setCategories(res.data);
            }
            setLoading(false);
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.category_name || !formData.category_code || !formData.depreciation_rate) {
            toast.error("Please fill all required fields");
            return;
        }

        if (editingCategory) {
            // Update
            updateassetcategory(editingCategory.id, formData).then((res) => {
                if (res.status === true) {
                    toast.success("Asset Category Updated Successfully");
                    fetchCategories();
                    resetForm();
                } else {
                    toast.error(res.message || "Failed to update");
                }
            });
        } else {
            // Create
            createassetcategory(formData).then((res) => {
                if (res.status === true) {
                    toast.success("Asset Category Created Successfully");
                    fetchCategories();
                    resetForm();
                } else {
                    toast.error(res.message || "Failed to create");
                }
            });
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            category_name: category.category_name,
            category_code: category.category_code,
            depreciation_method: category.depreciation_method,
            depreciation_rate: category.depreciation_rate,
            useful_life_years: category.useful_life_years || "",
            description: category.description || "",
            is_active: category.is_active
        });
        setShowForm(true);
    };

    const handleDelete = (category) => {
        if (category.asset_count > 0) {
            toast.error(`Cannot delete: ${category.asset_count} assets exist in this category`);
            return;
        }

        Swal.fire({
            title: 'Delete Asset Category?',
            text: `Delete ${category.category_name}? This cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteassetcategory(category.id).then((res) => {
                    if (res.status === true) {
                        toast.success("Asset Category Deleted Successfully");
                        fetchCategories();
                    } else {
                        toast.error(res.message || "Failed to delete");
                    }
                });
            }
        });
    };

    const resetForm = () => {
        setFormData({
            category_name: "",
            category_code: "",
            depreciation_method: "Straight Line",
            depreciation_rate: "",
            useful_life_years: "",
            description: "",
            is_active: true
        });
        setEditingCategory(null);
        setShowForm(false);
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Asset Categories</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Assets</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Asset Categories
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Asset Categories</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {!showForm && (
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => setShowForm(true)}
                                                >
                                                    <i className="fa fa-plus"></i> Create New Category
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {showForm && (
                                        <div className="card mb-4">
                                            <div className="card-header bg-light">
                                                <h5>{editingCategory ? 'Edit' : 'Create'} Asset Category</h5>
                                            </div>
                                            <div className="card-body">
                                                <form onSubmit={handleSubmit}>
                                                    <div className="row">
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">
                                                                Category Name <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="category_name"
                                                                value={formData.category_name}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., Furniture, Vehicles, Computers"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">
                                                                Category Code <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="category_code"
                                                                value={formData.category_code}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., FURN, VEH, COMP"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Depreciation Method <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="default-select  form-control wide"
                                                                name="depreciation_method"
                                                                value={formData.depreciation_method}
                                                                onChange={handleInputChange}
                                                                required
                                                            >
                                                                <option value="Straight Line">Straight Line</option>
                                                                <option value="Reducing Balance">Reducing Balance</option>
                                                                <option value="WDV">Written Down Value (WDV)</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Depreciation Rate (%) <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                name="depreciation_rate"
                                                                value={formData.depreciation_rate}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., 10, 15, 20"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Useful Life (Years)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="useful_life_years"
                                                                value={formData.useful_life_years}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., 5, 10"
                                                            />
                                                            <small className="text-muted">Required for Straight Line method</small>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <label className="form-label">Description</label>
                                                            <textarea
                                                                className="form-control"
                                                                name="description"
                                                                value={formData.description}
                                                                onChange={handleInputChange}
                                                                rows="2"
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    name="is_active"
                                                                    checked={formData.is_active}
                                                                    onChange={handleInputChange}
                                                                />
                                                                <label className="form-check-label">
                                                                    Active Category
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary me-2"
                                                                onClick={resetForm}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button type="submit" className="btn btn-primary">
                                                                <i className="fa fa-save"></i> {editingCategory ? 'Update' : 'Create'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    )}

                                    {/* Categories Table */}
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact columnDefs={columnDefs} rowData={categories} pagination={true} paginationPageSize={10} sortingOrder={["asc", "desc"]} suppressDragLeaveHidesColumns={true} domLayout="autoHeight" />
                                    </div>

                                    {/* Info Panel */}
                                    <div className="alert alert-info mt-4">
                                        <h6><i className="fa fa-info-circle"></i> Asset Category Tips:</h6>
                                        <ul className="mb-0">
                                            <li><strong>Straight Line:</strong> Equal depreciation each year. (Cost - Salvage) / Useful Life</li>
                                            <li><strong>Reducing Balance:</strong> Higher depreciation in early years. Rate applied to book value.</li>
                                            <li><strong>WDV (Written Down Value):</strong> Similar to Reducing Balance, commonly used in India.</li>
                                            <li><strong>Common Rates:</strong> Computers 40%, Furniture 10%, Buildings 5%, Vehicles 15%</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
